import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import * as api from "../api/client";
import { pixelTheme, type Theme } from "../theme/pixel";

const POLL_INTERVAL_MS = 8_000;

type QueueKind = "user" | "guest";

type QueueStatus =
  | { ready: true }
  | { ready: false; position: number };

/**
 * "Server full" page reached when a login attempt is rejected with a
 * 409 `capacity` because the lobby cap is full. The api/client's 409
 * handler navigates here and stashes the queue category in the URL so
 * the page can render the right copy (guest queues show a
 * sign-up-to-skip-the-line CTA; user queues just show position).
 *
 * Polls `/api/queue/status` every few seconds. When the server reports
 * `ready: true`, the page navigates back to `/login` and the SPA
 * retries — the queue cookie is still set, so the next login admits
 * the holder ahead of any new arrivals.
 */
export function ServerFullPage() {
  const theme = pixelTheme;
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const queueKind = (params.get("queue") ?? "user") as QueueKind;
  const [status, setStatus] = useState<QueueStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    async function poll() {
      try {
        const s = await api.queueStatus();
        if (cancelled.current) return;
        setError(null);
        setStatus(s);
        if (s.ready) {
          navigate("/login", { replace: true });
          return;
        }
      } catch (err) {
        if (cancelled.current) return;
        setError(err instanceof Error ? err.message : String(err));
      }
      timer = setTimeout(() => void poll(), POLL_INTERVAL_MS);
    }

    void poll();
    return () => {
      cancelled.current = true;
      if (timer) clearTimeout(timer);
    };
  }, [navigate]);

  const headline =
    queueKind === "guest" ? "GUEST SLOTS ARE FULL" : "SERVER AT CAPACITY";

  return (
    <div
      style={{
        minHeight: "100%",
        background: theme.pageBg,
        color: theme.ink,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: theme.fontBody,
        padding: 20,
      }}
    >
      <div
        style={{
          background: theme.panel,
          border: `2px solid ${theme.panelEdgeStrong}`,
          padding: 28,
          width: "100%",
          maxWidth: 460,
          display: "grid",
          gap: 18,
          textAlign: "center",
        }}
      >
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 18,
            letterSpacing: theme.letterSpacing,
            color: theme.accent,
          }}
        >
          {headline}
        </div>

        <PositionLine status={status} theme={theme} />

        <div style={{ fontSize: 13, color: theme.inkFaint, lineHeight: 1.5 }}>
          We&apos;ll let you in automatically when a slot frees up. Keep
          this page open.
        </div>

        {queueKind === "guest" && (
          <div
            style={{
              borderTop: `1px solid ${theme.panelEdge}`,
              paddingTop: 14,
              fontSize: 13,
              color: theme.inkDim,
              lineHeight: 1.5,
            }}
          >
            Or{" "}
            <Link to="/register" style={{ color: theme.accent }}>
              create an account
            </Link>{" "}
            to skip the guest queue.
          </div>
        )}

        {error && (
          <div style={{ color: theme.accent, fontSize: 12 }}>
            Couldn&apos;t reach the server: {error}. Retrying.
          </div>
        )}
      </div>
    </div>
  );
}

function PositionLine({
  status,
  theme,
}: {
  status: QueueStatus | null;
  theme: Theme;
}): JSX.Element {
  if (!status) {
    return (
      <div style={{ fontSize: 14, color: theme.inkDim }}>
        Checking your spot in line…
      </div>
    );
  }
  if (status.ready) {
    return (
      <div style={{ fontSize: 14, color: theme.inkDim }}>
        You&apos;re in. Redirecting…
      </div>
    );
  }
  return (
    <div
      style={{
        fontSize: 14,
        color: theme.inkDim,
        display: "grid",
        gap: 6,
      }}
    >
      <span>
        You&apos;re{" "}
        <strong style={{ color: theme.ink }}>#{status.position}</strong> in
        line.
      </span>
    </div>
  );
}
